import { ReactElement } from 'react'
import { Typography } from '@mui/material'
import BuildingForm from '@editor/components/BuildingForm'
import {
  useControlStore,
  useEditElementStore,
  useModelStore,
  useSelectionStore,
} from '@editorStores'
import { useElementType } from '@editorHooks'
import {
  LoadList,
  OpeningForm,
  PositionForm,
  PurlinForm,
  RoofSlabForm,
  SelectionForm,
  WallForm,
  WallRipForm,
  WallLintelForm,
  RoofForm,
  SlabForm,
  VerticalSlabsForm,
  MultiEdit,
} from '..'
import useStructuralPlanningSelection from '../../hooks/useStructuralPlanningSelection'
import { BeamForm, ColumnForm } from '../FloorplanDrawer/components'
import WallForm2D from '../FloorplanDrawer/components/WallForm'
import { FoundationPanel } from '../Foundation'
import { VerticalRoofForm } from '../VerticalRoof'
import { wallTabs } from '../WallForm/tabs'
import resolveDrawerContent from './resolveDrawerContent'

/**
 * Renders the drawer pane resolved by `resolveDrawerContent` — all decision logic lives in the
 * resolver; this component only maps the resolved descriptor to the concrete form components
 * (including the 2D floorplan variants the former FloorplanElementForm dispatched to).
 */
const StructuralPlanningDrawer = (): ReactElement | null => {
  const {
    params: { mode, selectedElements, storey },
    actions: { reset, toggleMode, setElements },
  } = useStructuralPlanningSelection()

  const storeMode = useSelectionStore(state => state.mode)
  const selection = useSelectionStore(state => state.selection)

  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const setActiveOpening = useEditElementStore(state => state.setActiveOpening)
  const activeOpening = useEditElementStore(state => state.activeOpening)

  const liveElementType = useElementType(selectedElements?.[0])
  const orthographicDrawMode = useControlStore(state => state.orthographicDrawMode)
  const walls = useModelStore(state => state.model.walls)

  const selectionElementType =
    (selection?.type === 'planar' ? selection.elementType : undefined) ??
    (liveElementType as PlanarSelectedElementType | undefined)

  const interactionMode = orthographicDrawMode ? '2D' : '3D'
  const isDrawModeActive = storeMode.startsWith('draw-')
  /** Close handler of element forms: draw modes only drop the element, others exit to neutral. */
  const closeElementForm = isDrawModeActive ? () => setActiveElement(null) : () => setElements([])

  const content = resolveDrawerContent({
    mode: storeMode,
    view: storey ? '2d' : '3d',
    selection,
    selectionElementType,
    activeOpeningGuid: activeOpening,
  })

  switch (content.kind) {
    case 'multi-edit':
      return <MultiEdit resetElements={() => setElements([])} selectedElements={content.guids} />

    case 'opening-form': {
      const wallGuid = content.fromPrimaryOpening
        ? content.openingGuid
          ? walls.find(w => w.openings?.some((o: Opening) => o.guid === content.openingGuid))
              ?.guid ?? null
          : null
        : content.wallGuid

      // Draw modes step back one rung: drop the opening first, then the wall (a further ESC then
      // leaves the mode via the page-level handler).
      const deselectInDrawMode = content.openingGuid
        ? () => setActiveOpening(null)
        : () => setActiveElement(null)

      return (
        <OpeningForm
          selectedWallId={wallGuid}
          selectedOpeningId={content.openingGuid}
          deselectWall={content.fromPrimaryOpening ? () => setElements([]) : deselectInDrawMode}
          setElements={setElements}
          toggleMode={toggleMode}
          mode={interactionMode}
          showDeselectButton
        />
      )
    }

    case 'stiffening-selection':
      return <SelectionForm onSubmitSuccess={() => toggleMode(undefined)} />

    case 'position-form':
      return (
        <PositionForm
          key={content.positionGuid}
          position={content.positionGuid}
          onClose={storey ? () => setActiveElement(null) : undefined}
        />
      )

    case 'wall-form':
      if (storey)
        return (
          <WallForm2D
            selectedElement={content.wallGuid}
            storeySelected={!!storey}
            onClose={() => setActiveElement(null)}
          />
        )

      return (
        <WallForm
          key={content.wallGuid}
          elementType={selectionElementType as ElementTypes}
          selectedElementGuid={content.wallGuid as string}
          onClose={reset}
          initialTabValue={wallTabs.geometry.tab}
        />
      )

    case 'roof-slab-form':
      return (
        <RoofSlabForm
          elementType={(selectionElementType ?? mode) as ElementTypes}
          selectedElements={content.guids}
          onClose={reset}
          selectElement={guid => setElements(guid ? [guid] : [])}
        />
      )

    case 'load-list':
      return <LoadList />

    case 'building-form':
      return <BuildingForm />

    case 'wall-rip-form':
      return (
        <WallRipForm
          selectedElement={content.wallGuid || undefined}
          onClose={storey ? () => setActiveElement(null) : () => setElements([])}
        />
      )

    case 'wall-lintel-form':
      return <WallLintelForm lintel={content.lintelGuid || undefined} />

    case 'beam-form':
      return (
        <BeamForm
          key={content.guid}
          selectedElement={content.guid as string}
          onClose={closeElementForm}
          interactionMode={interactionMode}
        />
      )

    case 'column-form':
      return (
        <ColumnForm
          key={content.guid}
          selectedElement={content.guid as string}
          activeElement={storey ? (content.guid as string) : undefined}
          geometryEditable={true}
          onClose={closeElementForm}
        />
      )

    case 'purlin-form':
      return (
        <PurlinForm
          key={content.guid}
          selectedElement={content.guid}
          onClose={closeElementForm}
          interactionMode={interactionMode}
        />
      )

    case 'vertical-roof-form':
      return (
        <VerticalRoofForm
          key={content.guid}
          selectedElement={content.guid || undefined}
          geometryEditable={true}
          activeStorey={storey || undefined}
          showDrawRoofOnElement={!!storey}
          onClose={closeElementForm}
        />
      )

    case 'vertical-slabs-form':
      return (
        <VerticalSlabsForm
          key={content.guid}
          selectedElement={content.guid as string}
          activeStorey={storey || undefined}
          showDrawSlabOnElement={!!storey}
          onClose={closeElementForm}
        />
      )

    case 'slab-form':
      return (
        <SlabForm
          selectedElement={content.guid}
          selectElement={setActiveElement}
          elementType="slabs"
        />
      )

    case 'roof-form':
      return (
        <RoofForm
          key={content.guid}
          selectedElement={content.guid}
          selectElement={setActiveElement}
        />
      )

    case 'foundation-panel':
      return (
        <FoundationPanel
          key={content.guid ?? 'foundation-panel'}
          selectedElement={content.guid}
          selectElement={element => setElements(element ? [element] : [])}
        />
      )

    case 'empty':
      if (content.reason === 'no-selection')
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2 }}
            data-cy="structural-planning-drawer-empty"
          >
            Kein Element ausgewählt
          </Typography>
        )
      return null

    default: {
      const exhaustivenessCheck: never = content
      void exhaustivenessCheck
      return null
    }
  }
}

export default StructuralPlanningDrawer
